import React from "react";
import { motion } from "framer-motion";
import { AlertCircle, FileText } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

interface OutstandingReportProps {
  data: {
    buckets: { label: string; totalCents: number; count: number }[];
    total: { totalCents: number; count: number };
  } | null;
  loading: boolean;
}

const BUCKET_COLORS: Record<string, string> = {
  "0-30 days": "#10b981",
  "31-60 days": "#f59e0b",
  "61-90 days": "#f97316",
  "90+ days": "#ef4444",
};

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const OutstandingReport: React.FC<OutstandingReportProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-32 animate-pulse">
            <div className="h-4 w-32 bg-slate-100 rounded mb-3" />
            <div className="h-8 w-24 bg-slate-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (!data) return null;

  const summaryCards = [
    {
      label: "Total Outstanding",
      value: formatCurrency(data.total.totalCents),
      icon: <AlertCircle className="h-5 w-5" />,
      color: "text-red-600",
      bg: "bg-red-50",
    },
    {
      label: "Number of Invoices",
      value: data.total.count.toString(),
      icon: <FileText className="h-5 w-5" />,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
  ];

  const chartData = data.buckets.map((b) => ({
    label: b.label,
    amount: b.totalCents / 100,
    count: b.count,
    color: BUCKET_COLORS[b.label] || "#64748b",
  }));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {summaryCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">{card.label}</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{card.value}</p>
              </div>
              <div className={`p-3 rounded-xl ${card.bg}`}>
                <span className={card.color}>{card.icon}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Aging Chart */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
      >
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Aging Breakdown</h3>
        {chartData.every((d) => d.amount === 0) ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="flex flex-col items-center justify-center py-16"
          >
            <div className="relative mb-5">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
              <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                <FileText className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
              </div>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1.5">No outstanding invoices</h3>
            <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">
              All caught up! Outstanding invoice aging will appear here when invoices are issued.
            </p>
          </motion.div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis
                tick={{ fontSize: 12, fill: "#64748b" }}
                tickFormatter={(v) => `$${v.toLocaleString()}`}
              />
              <Tooltip
                formatter={(value: number | string | undefined) => [formatCurrency(Number(value ?? 0) * 100), "Outstanding"]}
                contentStyle={{
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                }}
              />
              <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </motion.div>

      {/* Aging Table */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700">Aging Detail</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Age Bucket</th>
              <th className="text-right text-xs font-medium text-slate-500 uppercase px-6 py-3">Invoices</th>
              <th className="text-right text-xs font-medium text-slate-500 uppercase px-6 py-3">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.buckets.map((bucket) => (
              <tr key={bucket.label} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: BUCKET_COLORS[bucket.label] || "#64748b" }}
                    />
                    <span className="text-sm font-medium text-slate-700">{bucket.label}</span>
                  </div>
                </td>
                <td className="text-right text-sm text-slate-600 px-6 py-3">{bucket.count}</td>
                <td className="text-right text-sm font-medium text-slate-800 px-6 py-3">
                  {formatCurrency(bucket.totalCents)}
                </td>
              </tr>
            ))}
            <tr className="bg-slate-50 font-semibold">
              <td className="px-6 py-3 text-sm text-slate-700">Total</td>
              <td className="text-right text-sm text-slate-700 px-6 py-3">{data.total.count}</td>
              <td className="text-right text-sm text-slate-800 px-6 py-3">
                {formatCurrency(data.total.totalCents)}
              </td>
            </tr>
          </tbody>
        </table>
      </motion.div>
    </div>
  );
};

export default OutstandingReport;
